/**
 * Payment Service
 *
 * Handles all payment-related operations via API Gateway REST API.
 * Uses AuthenticatedApiClient for authenticated requests to the
 * Stripe payment backend (Lambda handlers).
 *
 * Features:
 * - Create Stripe customer for authenticated user
 * - Add payment method (attach tokenized card)
 * - List saved payment methods
 * - Remove payment method
 * - Set default payment method
 * - Create payment (credit purchase)
 *
 * Requirements: 2.3, 3.1, 10.2
 */

import { AuthenticatedApiClient } from './authenticated-api-client';
import type {
    PaymentMethod,
    Transaction,
    BillingInfo,
    StripeCustomer,
    CreatePaymentRequest,
    CreatePaymentResponse,
} from '../types/payment';

/**
 * Payment Service class for managing payment operations via API Gateway.
 */
export class PaymentService {
    private client: AuthenticatedApiClient;

    constructor() {
        this.client = new AuthenticatedApiClient(
            import.meta.env.DEV
                ? '/payment-api'
                : (import.meta.env.VITE_PAYMENT_API_URL || '/payment-api')
        );
    }

    /**
     * Creates or retrieves a Stripe customer for the authenticated user.
     *
     * @returns Promise resolving to the Stripe customer mapping
     */
    async createCustomer(): Promise<StripeCustomer> {
        try {
            const response = await this.client.apiRequest('/payments/create-customer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            return (await response.json()) as StripeCustomer;
        } catch (error) {
            throw new Error(
                `Failed to create customer: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Attaches a tokenized payment method to the authenticated user's Stripe customer.
     *
     * @param paymentMethodId - The Stripe payment method ID (pm_xxx) from Stripe.js
     * @returns Promise resolving to the saved payment method metadata
     */
    async addPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
        try {
            const response = await this.client.apiRequest('/payments/methods', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ payment_method_id: paymentMethodId }),
            });
            return (await response.json()) as PaymentMethod;
        } catch (error) {
            throw new Error(
                `Failed to add payment method: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Lists all saved payment methods for the authenticated user.
     *
     * @returns Promise resolving to an array of payment methods
     */
    async listPaymentMethods(): Promise<PaymentMethod[]> {
        try {
            const response = await this.client.apiRequest('/payments/methods', {
                method: 'GET',
            });
            const data = await response.json();
            return (data.payment_methods ?? data) as PaymentMethod[];
        } catch (error) {
            throw new Error(
                `Failed to list payment methods: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Removes a saved payment method from the authenticated user's Stripe customer.
     *
     * @param methodId - The Stripe payment method ID to remove
     */
    async removePaymentMethod(methodId: string): Promise<void> {
        try {
            await this.client.apiRequest(`/payments/methods/${methodId}`, {
                method: 'DELETE',
            });
        } catch (error) {
            throw new Error(
                `Failed to remove payment method: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Sets a payment method as the default for the authenticated user.
     *
     * @param methodId - The Stripe payment method ID to set as default
     */
    async setDefaultPaymentMethod(methodId: string): Promise<void> {
        try {
            await this.client.apiRequest(`/payments/methods/${methodId}/default`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
            });
        } catch (error) {
            throw new Error(
                `Failed to set default payment method: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Creates a payment (credit purchase) for the selected pricing tier.
     *
     * @param tierId - The pricing tier ID to purchase
     * @param paymentMethodId - Optional payment method ID; uses default if omitted
     * @returns Promise resolving to the payment response (success, 3DS, or error)
     */
    async createPayment(tierId: string, paymentMethodId?: string): Promise<CreatePaymentResponse> {
        try {
            const body: CreatePaymentRequest = { tier_id: tierId };
            if (paymentMethodId) {
                body.payment_method_id = paymentMethodId;
            }

            const response = await this.client.apiRequest('/payments/create-payment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            return (await response.json()) as CreatePaymentResponse;
        } catch (error) {
            throw new Error(
                `Failed to create payment: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    async getBillingInfo(): Promise<BillingInfo | null> {
        try {
            const response = await this.client.apiRequest('/payments/billing', { method: 'GET' });
            const data = await response.json();
            return data.billing ?? null;
        } catch (error) {
            throw new Error(`Failed to get billing info: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async updateBillingInfo(billing: Partial<BillingInfo>): Promise<BillingInfo> {
        try {
            const response = await this.client.apiRequest('/payments/billing', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(billing),
            });
            const data = await response.json();
            return data.billing;
        } catch (error) {
            throw new Error(`Failed to update billing info: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Lists transaction history for the authenticated user.
     */
    async listTransactions(): Promise<Transaction[]> {
        try {
            const response = await this.client.apiRequest('/payments/transactions', {
                method: 'GET',
            });
            const data = await response.json();
            return (data.transactions ?? data) as Transaction[];
        } catch (error) {
            throw new Error(
                `Failed to list transactions: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }
}
